import { useEffect, useState } from 'react';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-[1440px] mx-auto px-20 py-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="https://public.readdy.ai/ai/img_res/98b69e61-5f01-4bcf-9d90-aa7adfe7e345.png" alt="DISER LTDA Logo" className="h-12 w-auto" />
            <span className="text-lg font-extrabold tracking-wider text-black">DISER LTDA</span>
          </div>
          <div className="flex items-center gap-12">
            <button onClick={() => scrollToSection('home')} className="text-base font-medium text-gray-700 hover:text-black transition-colors cursor-pointer whitespace-nowrap">Home</button>
            <button onClick={() => scrollToSection('about')} className="text-base font-medium text-gray-700 hover:text-black transition-colors cursor-pointer whitespace-nowrap">About</button>
            <button onClick={() => scrollToSection('products')} className="text-base font-medium text-gray-700 hover:text-black transition-colors cursor-pointer whitespace-nowrap">Products</button>
            <button onClick={() => scrollToSection('contact')} className="text-base font-medium text-gray-700 hover:text-black transition-colors cursor-pointer whitespace-nowrap">Contact</button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-50"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
        
        {/* Floating decorative elements */}
        <div className="absolute top-1/4 right-1/4 w-64 h-64 bg-gradient-to-br from-purple-200/40 to-blue-200/40 rounded-[32px] rotate-12 blur-sm"></div>
        <div className="absolute top-1/3 right-1/3 w-48 h-48 bg-gradient-to-br from-indigo-200/40 to-purple-200/40 rounded-[32px] -rotate-6 blur-sm"></div>
        <div className="absolute bottom-1/3 right-1/4 w-56 h-56 bg-gradient-to-br from-blue-200/40 to-cyan-200/40 rounded-[32px] rotate-6 blur-sm"></div>

        <div className="relative max-w-[1440px] mx-auto px-20 py-32 w-full">
          <div className="flex items-center gap-16">
            <div className="flex-1">
              <div className="relative">
                <div className="absolute -left-20 top-8 w-1 h-32 bg-red-500"></div>
                <h1 className="text-[96px] font-extrabold leading-[120px] text-black mb-8">
                  Modern iOS<br />Applications
                </h1>
              </div>
              <p className="text-[22px] font-medium text-gray-700 max-w-[520px] leading-relaxed">
                A team of experts developing cutting-edge applications for the iOS platform. Our products, including VPN and Cleaner, are designed to enhance user experience and improve the security of mobile devices.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-32 bg-gray-50">
        <div className="max-w-[1440px] mx-auto px-20">
          <div className="flex items-start justify-between mb-16">
            <div>
              <h2 className="text-[64px] font-bold text-black mb-4">Our Products</h2>
              <p className="text-[28px] font-light text-red-500">VPN &amp; Cleaner</p>
            </div>
            <div className="bg-black text-white px-6 py-3 rounded-full text-sm font-medium whitespace-nowrap">
              iOS Exclusive
            </div>
          </div>

          <div className="overflow-x-auto pb-8 -mx-20 px-20" style={{ scrollbarWidth: 'thin' }}>
            <div className="flex gap-8 min-w-max">
              {/* VPN Product Card */}
              <div className="w-[420px] bg-white rounded-[28px] shadow-lg p-10 flex flex-col" data-product-shop>
                <div className="w-full h-[280px] flex items-center justify-center mb-8 bg-gradient-to-br from-purple-400 to-blue-500 rounded-3xl">
                  <i className="ri-shield-check-line text-white text-[120px]"></i>
                </div>
                <h3 className="text-[32px] font-bold text-black text-center mb-2">VPN Pro</h3>
                <p className="text-sm text-gray-500 text-center mb-8">Security Tool</p>
                <ul className="space-y-6">
                  <li className="flex items-start gap-3 text-base text-gray-600">
                    <i className="ri-check-line text-green-500 text-xl mt-0.5"></i>
                    <span>Military-grade encryption for ultimate privacy</span>
                  </li>
                  <li className="flex items-start gap-3 text-base text-gray-600">
                    <i className="ri-check-line text-green-500 text-xl mt-0.5"></i>
                    <span>Lightning-fast servers in 50+ countries</span>
                  </li>
                  <li className="flex items-start gap-3 text-base text-gray-600">
                    <i className="ri-check-line text-green-500 text-xl mt-0.5"></i>
                    <span>No-logs policy with automatic kill switch</span>
                  </li>
                </ul>
              </div>

              {/* Cleaner Product Card */}
              <div className="w-[420px] bg-white rounded-[28px] shadow-lg p-10 flex flex-col" data-product-shop>
                <div className="w-full h-[280px] flex items-center justify-center mb-8 bg-gradient-to-br from-green-400 to-teal-500 rounded-3xl">
                  <i className="ri-brush-line text-white text-[120px]"></i>
                </div>
                <h3 className="text-[32px] font-bold text-black text-center mb-2">Smart Cleaner</h3>
                <p className="text-sm text-gray-500 text-center mb-8">Optimization Tool</p>
                <ul className="space-y-6">
                  <li className="flex items-start gap-3 text-base text-gray-600">
                    <i className="ri-check-line text-green-500 text-xl mt-0.5"></i>
                    <span>Intelligent storage optimization algorithms</span>
                  </li>
                  <li className="flex items-start gap-3 text-base text-gray-600">
                    <i className="ri-check-line text-green-500 text-xl mt-0.5"></i>
                    <span>One-tap junk file removal and cleanup</span>
                  </li>
                  <li className="flex items-start gap-3 text-base text-gray-600">
                    <i className="ri-check-line text-green-500 text-xl mt-0.5"></i>
                    <span>Battery life enhancement features</span>
                  </li>
                </ul>
              </div>

              {/* Additional Feature Cards */}
              <div className="w-[420px] bg-white rounded-[28px] shadow-lg p-10 flex flex-col" data-product-shop>
                <div className="w-full h-[280px] flex items-center justify-center mb-8 bg-gradient-to-br from-orange-400 to-red-500 rounded-3xl">
                  <i className="ri-speed-up-line text-white text-[120px]"></i>
                </div>
                <h3 className="text-[32px] font-bold text-black text-center mb-2">Performance Boost</h3>
                <p className="text-sm text-gray-500 text-center mb-8">System Enhancement</p>
                <ul className="space-y-6">
                  <li className="flex items-start gap-3 text-base text-gray-600">
                    <i className="ri-check-line text-green-500 text-xl mt-0.5"></i>
                    <span>Real-time device performance monitoring</span>
                  </li>
                  <li className="flex items-start gap-3 text-base text-gray-600">
                    <i className="ri-check-line text-green-500 text-xl mt-0.5"></i>
                    <span>Memory optimization and management</span>
                  </li>
                  <li className="flex items-start gap-3 text-base text-gray-600">
                    <i className="ri-check-line text-green-500 text-xl mt-0.5"></i>
                    <span>App launch speed acceleration</span>
                  </li>
                </ul>
              </div>

              <div className="w-[420px] bg-white rounded-[28px] shadow-lg p-10 flex flex-col" data-product-shop>
                <div className="w-full h-[280px] flex items-center justify-center mb-8 bg-gradient-to-br from-pink-400 to-rose-500 rounded-3xl">
                  <i className="ri-lock-password-line text-white text-[120px]"></i>
                </div>
                <h3 className="text-[32px] font-bold text-black text-center mb-2">Privacy Guard</h3>
                <p className="text-sm text-gray-500 text-center mb-8">Security Suite</p>
                <ul className="space-y-6">
                  <li className="flex items-start gap-3 text-base text-gray-600">
                    <i className="ri-check-line text-green-500 text-xl mt-0.5"></i>
                    <span>Advanced app permission management</span>
                  </li>
                  <li className="flex items-start gap-3 text-base text-gray-600">
                    <i className="ri-check-line text-green-500 text-xl mt-0.5"></i>
                    <span>Secure photo and file vault protection</span>
                  </li>
                  <li className="flex items-start gap-3 text-base text-gray-600">
                    <i className="ri-check-line text-green-500 text-xl mt-0.5"></i>
                    <span>Biometric authentication integration</span>
                  </li>
                </ul>
              </div>

              <div className="w-[420px] bg-white rounded-[28px] shadow-lg p-10 flex flex-col" data-product-shop>
                <div className="w-full h-[280px] flex items-center justify-center mb-8 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-3xl">
                  <i className="ri-wifi-line text-white text-[120px]"></i>
                </div>
                <h3 className="text-[32px] font-bold text-black text-center mb-2">Network Shield</h3>
                <p className="text-sm text-gray-500 text-center mb-8">Connection Security</p>
                <ul className="space-y-6">
                  <li className="flex items-start gap-3 text-base text-gray-600">
                    <i className="ri-check-line text-green-500 text-xl mt-0.5"></i>
                    <span>Public Wi-Fi protection and encryption</span>
                  </li>
                  <li className="flex items-start gap-3 text-base text-gray-600">
                    <i className="ri-check-line text-green-500 text-xl mt-0.5"></i>
                    <span>Malicious website blocking technology</span>
                  </li>
                  <li className="flex items-start gap-3 text-base text-gray-600">
                    <i className="ri-check-line text-green-500 text-xl mt-0.5"></i>
                    <span>Real-time threat detection alerts</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-32">
        <div className="max-w-[1440px] mx-auto px-20">
          <div className="grid grid-cols-2 gap-24 items-center">
            <div>
              <div className="text-xs uppercase tracking-[3px] text-gray-500 font-medium mb-6">ABOUT DISER LTDA</div>
              <h2 className="text-[56px] font-bold text-black leading-tight mb-10">
                Experts in iOS<br />Development
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed max-w-[440px] mb-12">
                We are a dedicated team of iOS specialists committed to creating innovative applications that transform the mobile experience. Our expertise spans security, optimization, and user-centric design, ensuring every product we develop meets the highest standards of quality and performance.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed max-w-[440px]">
                With years of experience in the iOS ecosystem, we understand the unique challenges and opportunities of mobile development. Our mission is to deliver solutions that not only solve problems but also delight users with intuitive interfaces and powerful functionality.
              </p>
            </div>

            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-orange-50 to-pink-50 rounded-3xl"></div>
              <div className="relative p-16">
                <div className="w-full h-[400px] rounded-2xl overflow-hidden bg-cover bg-center" style={{ backgroundImage: 'url(https://public.readdy.ai/ai/img_res/08deb21bdd5fc1f994723d1c60f98674.jpg)' }}></div>
              </div>

              {/* Decorative blobs */}
              <div className="absolute -top-8 -right-8 w-32 h-32 bg-pink-200/40 rounded-full blur-2xl"></div>
              <div className="absolute -bottom-8 -left-8 w-40 h-40 bg-orange-200/40 rounded-full blur-2xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Technology Features */}
      <section className="py-32 bg-gray-50">
        <div className="max-w-[1440px] mx-auto px-20">
          <div className="text-center mb-20">
            <div className="inline-block border border-gray-300 rounded-full px-6 py-2 text-sm text-gray-600 mb-6">
              Our Technology
            </div>
            <h2 className="text-[52px] font-bold text-black">
              <span className="font-light italic text-gray-500">Cutting-Edge</span><br />
              iOS Solutions
            </h2>
          </div>

          <div className="grid grid-cols-3 gap-8">
            {/* Feature Card 1 */}
            <div className="bg-white rounded-3xl shadow-lg overflow-hidden">
              <div className="w-full h-[400px] bg-cover bg-center" style={{ backgroundImage: 'url(https://readdy.ai/api/search-image?query=Modern%20smartphone%20displaying%20advanced%20security%20interface%20with%20shield%20icons%20and%20encryption%20symbols%20on%20a%20clean%20minimalist%20gradient%20background%20featuring%20soft%20purple%20and%20blue%20tones%20professional%20technology%20photography%20with%20sharp%20focus%20and%20elegant%20composition&width=400&height=400&seq=tech1&orientation=squarish)' }}></div>
              <div className="p-6">
                <div className="inline-block bg-lime-400 text-black text-xs font-medium px-3 py-1 rounded-full mb-4">Security</div>
                <h3 className="text-[28px] font-bold text-black leading-tight">Advanced Encryption Protocols</h3>
              </div>
            </div>

            {/* Feature Card 2 */}
            <div className="bg-gray-900 rounded-3xl shadow-lg overflow-hidden flex flex-col items-center justify-center p-12">
              <div className="w-48 h-48 flex items-center justify-center mb-8">
                <i className="ri-cpu-line text-lime-400 text-[120px]"></i>
              </div>
              <div className="inline-block bg-lime-400 text-black text-xs font-medium px-3 py-1 rounded-full mb-4">Performance</div>
              <h3 className="text-[28px] font-bold text-white leading-tight text-center">AI-Powered Optimization</h3>
            </div>

            {/* Feature Card 3 */}
            <div className="bg-white rounded-3xl shadow-lg overflow-hidden">
              <div className="w-full h-[280px] bg-cover bg-center" style={{ backgroundImage: 'url(https://readdy.ai/api/search-image?query=Elegant%20iOS%20device%20interface%20showing%20clean%20user%20experience%20design%20with%20modern%20UI%20elements%20on%20a%20simple%20gradient%20background%20with%20soft%20blue%20and%20white%20tones%20minimalist%20product%20photography%20with%20professional%20lighting&width=400&height=280&seq=tech2&orientation=squarish)' }}></div>
              <div className="p-6">
                <div className="inline-block bg-lime-400 text-black text-xs font-medium px-3 py-1 rounded-full mb-4">Design</div>
                <h3 className="text-[28px] font-bold text-black leading-tight">Intuitive User Interface</h3>
              </div>
            </div>

            {/* Feature Card 4 */}
            <div className="bg-white rounded-3xl shadow-lg overflow-hidden">
              <div className="w-full h-[280px] bg-cover bg-center" style={{ backgroundImage: 'url(https://readdy.ai/api/search-image?query=Modern%20cloud%20computing%20visualization%20with%20network%20connections%20and%20data%20streams%20on%20a%20clean%20gradient%20background%20featuring%20soft%20cyan%20and%20blue%20tones%20abstract%20technology%20illustration%20with%20geometric%20elements&width=400&height=280&seq=tech3&orientation=squarish)' }}></div>
              <div className="p-6">
                <div className="inline-block bg-lime-400 text-black text-xs font-medium px-3 py-1 rounded-full mb-4">Infrastructure</div>
                <h3 className="text-[28px] font-bold text-black leading-tight">Cloud-Based Architecture</h3>
              </div>
            </div>

            {/* Feature Card 5 */}
            <div className="bg-white rounded-3xl shadow-lg overflow-hidden">
              <div className="w-full h-[400px] bg-cover bg-center" style={{ backgroundImage: 'url(https://readdy.ai/api/search-image?query=Smartphone%20displaying%20real-time%20analytics%20dashboard%20with%20graphs%20and%20metrics%20on%20a%20clean%20minimalist%20background%20with%20soft%20green%20and%20teal%20gradient%20professional%20technology%20photography%20with%20modern%20aesthetic&width=400&height=400&seq=tech4&orientation=squarish)' }}></div>
              <div className="p-6">
                <div className="inline-block bg-lime-400 text-black text-xs font-medium px-3 py-1 rounded-full mb-4">Analytics</div>
                <h3 className="text-[28px] font-bold text-black leading-tight">Real-Time Performance Monitoring</h3>
              </div>
            </div>

            {/* Feature Card 6 */}
            <div className="bg-gray-900 rounded-3xl shadow-lg overflow-hidden flex flex-col items-center justify-center p-12">
              <div className="w-48 h-48 flex items-center justify-center mb-8">
                <i className="ri-shield-check-line text-lime-400 text-[120px]"></i>
              </div>
              <div className="inline-block bg-lime-400 text-black text-xs font-medium px-3 py-1 rounded-full mb-4">Privacy</div>
              <h3 className="text-[28px] font-bold text-white leading-tight text-center">Zero-Knowledge Architecture</h3>
            </div>
          </div>
        </div>
      </section>

      {/* Contact/Footer Section */}
      <footer id="contact" className="bg-black text-white">
        <div className="max-w-[1440px] mx-auto px-20 py-20">
          <div className="grid grid-cols-12 gap-16 mb-16">
            {/* Left Column - Brand */}
            <div className="col-span-5">
              <h3 className="text-[88px] font-extrabold text-white leading-none mb-32">DISER LTDA</h3>
              <p className="text-base text-white/80 leading-relaxed max-w-[360px] mb-20">
                A team of experts developing modern applications for the iOS platform. Our products are designed to enhance user experience and improve the security of mobile devices.
              </p>
              <div className="flex items-center gap-6">
                <a href="#" className="w-12 h-12 rounded-full border-2 border-white flex items-center justify-center hover:bg-white hover:text-black transition-colors cursor-pointer">
                  <i className="ri-instagram-line text-xl"></i>
                </a>
                <a href="#" className="w-12 h-12 rounded-full border-2 border-white flex items-center justify-center hover:bg-white hover:text-black transition-colors cursor-pointer">
                  <i className="ri-twitter-x-line text-xl"></i>
                </a>
                <a href="#" className="w-12 h-12 rounded-full border-2 border-white flex items-center justify-center hover:bg-white hover:text-black transition-colors cursor-pointer">
                  <i className="ri-linkedin-line text-xl"></i>
                </a>
                <a href="#" className="w-12 h-12 rounded-full border-2 border-white flex items-center justify-center hover:bg-white hover:text-black transition-colors cursor-pointer">
                  <i className="ri-github-line text-xl"></i>
                </a>
              </div>
            </div>

            {/* Middle Column - Contact Info */}
            <div className="col-span-4 space-y-12">
              <div>
                <div className="text-xs uppercase tracking-[2px] text-white/60 font-medium mb-4">ADDRESS</div>
                <div className="text-sm text-white leading-relaxed">
                  CALLE 18 6 32<br />
                  SANTA MARTA
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-[2px] text-white/60 font-medium mb-4">LOCATION</div>
                <div className="text-sm text-white leading-relaxed">
                  470001<br />
                  Colombia
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-[2px] text-white/60 font-medium mb-4">PHONE</div>
                <div className="text-sm text-white">+1 6207985473</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-[2px] text-white/60 font-medium mb-4">EMAIL</div>
                <div className="text-sm text-white">info@diserltda.com</div>
              </div>
            </div>

            {/* Right Column - Image */}
            <div className="col-span-3">
              <div className="w-full h-[400px] rounded-2xl border border-white/10 overflow-hidden bg-cover bg-center" style={{ backgroundImage: 'url(https://readdy.ai/api/search-image?query=Modern%20office%20workspace%20with%20multiple%20iOS%20devices%20including%20iPhones%20and%20iPads%20on%20a%20clean%20desk%20setup%20with%20soft%20natural%20lighting%20and%20minimalist%20design%20professional%20technology%20photography%20with%20warm%20tones&width=280&height=400&seq=footer1&orientation=portrait)' }}></div>
            </div>
          </div>

          {/* Bottom Copyright */}
          <div className="border-t border-white/15 pt-10">
            <div className="flex items-center justify-between">
              <div className="text-[13px] text-white/80">© 2025 DISER LTDA. All rights reserved.</div>
              <div className="flex items-center gap-8">
                <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-[13px] text-white/80 hover:text-white transition-colors cursor-pointer whitespace-nowrap">Website Builder</a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
